/**
 * Stub guides for remaining modules — each follows the same pattern.
 * PurchaseOrdersGuide, RfxSourcingGuide, AssetRegisterGuide, InvoiceMatchingGuide,
 * InventoryGuide, GoodsReceivingGuide, ReportsGuide
 */
import React from 'react';
import GuideViewer, { GuideSection, GuideSubSection, GuideCallout, GuideFieldTable, GuideTOC } from './GuideViewer';
import AppShell from '../../components/layout/AppShell';

/* ═══════════════════════════════════════════════════════════════
   RFx SOURCING GUIDE
   ═══════════════════════════════════════════════════════════════ */
function RfxContent() {
  const toc = [
    { id: 'overview', label: 'What is RFx Sourcing?' },
    { id: 'dashboard', label: 'RFx Dashboard' },
    { id: 'event-types', label: 'Event Types' },
    { id: 'creating', label: 'Creating a Sourcing Event' },
    { id: 'wizard', label: 'The Sourcing Event Wizard' },
    { id: 'suppliers-tab', label: 'Managing Suppliers & Invitations' },
    { id: 'responses', label: 'Supplier Responses & Evaluation' },
    { id: 'auctions', label: 'Live Reverse Auctions' },
    { id: 'award', label: 'Award Decisions' },
    { id: 'savings', label: 'Savings Tracking' },
    { id: 'reports', label: 'RFx Reports' },
  ];

  return (
    <GuideViewer title="RFx Sourcing Module" subtitle="Competitive Sourcing User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. What is RFx Sourcing?">
        <p className="text-sm text-slate-600 mb-3">
          RFx Sourcing enables your organisation to run competitive procurement events to find the best suppliers at the best prices. "RFx" is a collective term covering Request for Quotation (RFQ), Request for Proposal (RFP), Request for Information (RFI), and Reverse Auctions.
        </p>
        <p className="text-sm text-slate-600 mb-3">
          The module provides a complete workflow: create an event, define requirements, invite suppliers, collect responses, evaluate bids, run live auctions, make award decisions, and track savings achieved.
        </p>
      </GuideSection>

      <GuideSection id="dashboard" title="2. RFx Dashboard">
        <GuideFieldTable fields={[
          { name: 'Total Events', desc: 'Total sourcing events in the system.' },
          { name: 'Open', desc: 'Events currently accepting supplier responses.' },
          { name: 'In Evaluation', desc: 'Events where responses are being reviewed.' },
          { name: 'Awarded', desc: 'Events where suppliers have been selected.' },
          { name: 'Suppliers Invited', desc: 'Total number of supplier invitations sent.' },
          { name: 'Responses', desc: 'Total responses received from suppliers.' },
          { name: 'Closing Within 7 Days', desc: 'Events approaching their deadline.' },
          { name: 'Charts', desc: 'Event status breakdown, events by type, creation trend, and category distribution.' },
        ]} />
      </GuideSection>

      <GuideSection id="event-types" title="3. Event Types">
        <GuideFieldTable fields={[
          { name: 'RFQ (Request for Quotation)', desc: 'Used when you know exactly what you need and want competitive pricing. Suppliers submit unit prices for specified items.' },
          { name: 'RFP (Request for Proposal)', desc: 'Used for complex requirements where you need both technical proposals and pricing. Evaluation includes qualitative criteria.' },
          { name: 'RFI (Request for Information)', desc: 'Used for market research. No commitment to purchase. Gather information about supplier capabilities and offerings.' },
          { name: 'Reverse Auction', desc: 'A real-time competitive bidding event where suppliers bid downwards against each other. The lowest bid wins. Includes live monitoring and automatic extensions.' },
        ]} />
      </GuideSection>

      <GuideSection id="creating" title="4. Creating a Sourcing Event">
        <p className="text-sm text-slate-600 mb-3">
          Click <strong>+ Create Event</strong> on the RFx page to launch the Sourcing Event Wizard.
        </p>
      </GuideSection>

      <GuideSection id="wizard" title="5. The Sourcing Event Wizard">
        <p className="text-sm text-slate-600 mb-3">
          The wizard walks you through event creation in steps:
        </p>
        <GuideFieldTable fields={[
          { name: 'Event Details', desc: 'Title, type (RFQ/RFP/RFI/Auction), description, category, currency, closing date, and any linked requisitions.' },
          { name: 'Line Items', desc: 'The items/lots being sourced. Each has a description, quantity, unit, and optional target price.' },
          { name: 'Evaluation Criteria', desc: 'For RFPs: define weighted criteria (price, quality, delivery, etc.) used to score responses.' },
          { name: 'Questions', desc: 'Custom questions for suppliers to answer as part of their response.' },
          { name: 'Attachments', desc: 'Upload specifications, drawings, or other documents for suppliers to reference.' },
          { name: 'Terms & Conditions', desc: 'Attach standard terms that suppliers must accept.' },
          { name: 'Suppliers', desc: 'Select which suppliers to invite from your supplier master.' },
        ]} />
      </GuideSection>

      <GuideSection id="suppliers-tab" title="6. Managing Suppliers & Invitations">
        <p className="text-sm text-slate-600 mb-3">
          The Suppliers tab on each event shows invited suppliers and their response status. You can:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>Add more suppliers after event creation</li>
          <li>Send/resend invitation emails</li>
          <li>View invitation status (Sent, Viewed, Responded, Declined)</li>
          <li>Track which suppliers have submitted responses</li>
        </ul>
      </GuideSection>

      <GuideSection id="responses" title="7. Supplier Responses & Evaluation">
        <p className="text-sm text-slate-600 mb-3">
          After the closing date, evaluate supplier responses:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>Compare prices side-by-side across suppliers</li>
          <li>Score qualitative criteria (for RFPs)</li>
          <li>Review supplier answers to custom questions</li>
          <li>Calculate total cost of ownership</li>
        </ul>
      </GuideSection>

      <GuideSection id="auctions" title="8. Live Reverse Auctions">
        <p className="text-sm text-slate-600 mb-3">
          Reverse Auctions are conducted in real-time through the <strong>Auction Control Room</strong>:
        </p>
        <GuideFieldTable fields={[
          { name: 'Control Room', desc: 'The buyer\'s view showing all bids in real-time, current rankings, and auction controls.' },
          { name: 'Supplier Bidding Screen', desc: 'The supplier portal view where suppliers enter bids and see their ranking (not other bids).' },
          { name: 'Auto-Extension', desc: 'If a bid is placed in the final minutes, the auction is automatically extended to prevent sniping.' },
          { name: 'Chat', desc: 'Real-time messaging between the auction administrator and suppliers.' },
          { name: 'Bid History', desc: 'Complete log of all bids placed, timestamped.' },
        ]} />
      </GuideSection>

      <GuideSection id="award" title="9. Award Decisions">
        <p className="text-sm text-slate-600 mb-3">
          After evaluation, make award decisions specifying which supplier(s) won, the quantities awarded, and the rationale. Awards can be:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li><strong>Single Award</strong> — One supplier wins the entire event</li>
          <li><strong>Split Award</strong> — Different items awarded to different suppliers</li>
          <li><strong>Partial Award</strong> — Only some items are awarded; others are re-sourced</li>
        </ul>
      </GuideSection>

      <GuideSection id="savings" title="10. Savings Tracking">
        <p className="text-sm text-slate-600 mb-3">
          Eyvo automatically calculates procurement savings by comparing the awarded price against a baseline (previous price, budget estimate, or highest bid). Savings are reported on the Savings Report page and in the dashboard.
        </p>
      </GuideSection>

      <GuideSection id="reports" title="11. RFx Reports">
        <p className="text-sm text-slate-600 mb-3">
          Reports include event listings, response analysis, award traceability, and savings summaries. The <strong>Traceability Report</strong> provides a complete audit trail for regulatory compliance.
        </p>
      </GuideSection>
    </GuideViewer>
  );
}

export function RfxSourcingGuidePage() { return <AppShell><RfxContent /></AppShell>; }
export function RfxSourcingGuideEmbed() { return <RfxContent />; }

/* ═══════════════════════════════════════════════════════════════
   PURCHASE ORDERS GUIDE
   ═══════════════════════════════════════════════════════════════ */
function POContent() {
  const toc = [
    { id: 'overview', label: 'What is a Purchase Order?' },
    { id: 'dashboard', label: 'PO Dashboard' },
    { id: 'listing', label: 'PO Listing' },
    { id: 'creating', label: 'Creating a Purchase Order' },
    { id: 'detail', label: 'PO Detail View' },
    { id: 'line-items', label: 'Line Items' },
    { id: 'approval', label: 'Approval Workflow' },
    { id: 'amendments', label: 'Amendments' },
    { id: 'dispatch', label: 'Dispatching to Suppliers' },
    { id: 'grn', label: 'Receiving' },
    { id: 'invoices', label: 'Linked Invoices' },
    { id: 'reports', label: 'PO Reports' },
  ];

  return (
    <GuideViewer title="Purchase Orders Module" subtitle="Purchase Order Management User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. What is a Purchase Order?">
        <p className="text-sm text-slate-600 mb-3">
          A Purchase Order (PO) is a formal document issued to a supplier authorising a purchase of goods or services at specified prices and terms. It is a legally binding commitment between your organisation and the supplier. POs typically originate from approved requisitions but can also be created directly.
        </p>
      </GuideSection>

      <GuideSection id="dashboard" title="2. PO Dashboard">
        <GuideFieldTable fields={[
          { name: 'Open POs', desc: 'Number of POs in open/active status.' },
          { name: 'Open PO Value', desc: 'Total value of all open purchase orders.' },
          { name: 'Spend MTD', desc: 'Total spend month-to-date with percentage change.' },
          { name: 'Pending Approvals', desc: 'POs waiting for approval.' },
          { name: 'Overdue POs', desc: 'POs past their expected delivery date.' },
          { name: 'Avg Cycle Time', desc: 'Average time from PO creation to goods receipt.' },
          { name: 'Operational Risks', desc: 'Approval aging, overdue deliveries, due next 7 days, and high-value pending.' },
          { name: 'Charts', desc: 'Status breakdown, spend trend, spend by category, and top 10 suppliers.' },
          { name: 'Supplier Performance', desc: 'On-time delivery percentage, average delay, and delay table.' },
        ]} />
      </GuideSection>

      <GuideSection id="listing" title="3. PO Listing">
        <GuideFieldTable fields={[
          { name: 'Order Number', desc: 'System-generated PO reference (e.g., "PO000023").' },
          { name: 'Status', desc: 'Current status: Draft, Unapproved, Pending, Approved, Dispatched, Partial, Closed, Cancelled.' },
          { name: 'Supplier', desc: 'The supplier the order is placed with.' },
          { name: 'Order Date', desc: 'Date the PO was created.' },
          { name: 'Net Total', desc: 'Total value of the order.' },
          { name: 'Description', desc: 'Brief description of the order.' },
          { name: 'Request Number', desc: 'Linked requisition (if converted from a PR).' },
        ]} />
      </GuideSection>

      <GuideSection id="creating" title="4. Creating a Purchase Order">
        <p className="text-sm text-slate-600 mb-3">
          POs can be created in two ways:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 mb-3">
          <li><strong>From a Requisition</strong> — Convert an approved requisition. All details carry forward automatically.</li>
          <li><strong>Direct Creation</strong> — Click "+ New Order" to create a PO from scratch.</li>
        </ul>
      </GuideSection>

      <GuideSection id="detail" title="5. PO Detail View">
        <p className="text-sm text-slate-600 mb-3">
          The PO detail page shows a header strip with key information (order number, status, date, supplier, approval requirements), followed by tabbed content:
        </p>
        <GuideFieldTable fields={[
          { name: 'Header Details', desc: 'Supplier, delivery point, invoice point, currency, incoterms, shipping method, and notes.' },
          { name: 'Line Items', desc: 'All items being ordered with quantities, prices, and account codes.' },
          { name: 'Receiving', desc: 'GRN records showing what has been delivered against this PO.' },
          { name: 'Invoices', desc: 'Invoices matched against this PO.' },
          { name: 'Attachments', desc: 'Supporting documents.' },
          { name: 'Approval History', desc: 'Who approved/rejected and when.' },
          { name: 'Version History', desc: 'Amendment history showing each version of the PO.' },
        ]} />
      </GuideSection>

      <GuideSection id="line-items" title="6. Line Items">
        <p className="text-sm text-slate-600 mb-3">
          PO line items are similar to requisition line items but with additional fields for the order context:
        </p>
        <GuideFieldTable fields={[
          { name: 'Item Code / Description', desc: 'What is being ordered.' },
          { name: 'Quantity / Unit / Pack Size', desc: 'How much is being ordered.' },
          { name: 'Unit Price / Discount', desc: 'Agreed pricing.' },
          { name: 'Net / Tax / Gross Price', desc: 'Calculated totals per line.' },
          { name: 'Item Due Date', desc: 'Expected delivery date for this line.' },
          { name: 'Account Codes', desc: 'Budget allocation.' },
          { name: 'Received Qty', desc: 'How much has been received via GRN (read-only, updated by Receiving).' },
        ]} />
      </GuideSection>

      <GuideSection id="approval" title="7. Approval Workflow">
        <p className="text-sm text-slate-600 mb-3">
          POs follow a similar approval workflow to requisitions. Three approval types may apply:
        </p>
        <GuideFieldTable fields={[
          { name: 'Rule Approval', desc: 'Based on configurable rules (value thresholds, document types).' },
          { name: 'Cost Centre Approval', desc: 'Based on the cost centre/account codes on the PO.' },
          { name: 'Group Approval', desc: 'Based on user group assignments.' },
        ]} />
      </GuideSection>

      <GuideSection id="amendments" title="8. Amendments">
        <p className="text-sm text-slate-600 mb-3">
          After a PO has been dispatched, any changes create an <strong>Amendment</strong>. The amendment is a new version of the PO that must go through approval again. The Version History tab shows all amendments with their dates, authors, and reasons.
        </p>
      </GuideSection>

      <GuideSection id="dispatch" title="9. Dispatching to Suppliers">
        <p className="text-sm text-slate-600 mb-3">
          Once approved, click <strong>Dispatch</strong> to send the PO to the supplier. Dispatch can be done via email (PDF attachment) or through the Supplier Portal. The PO status changes to "DISPATCHED".
        </p>
      </GuideSection>

      <GuideSection id="grn" title="10. Receiving">
        <p className="text-sm text-slate-600 mb-3">
          When goods arrive, a Goods Received Note (GRN) is created against the PO from the Receiving tab. See the <strong>Receiving Module Guide</strong> for details.
        </p>
      </GuideSection>

      <GuideSection id="invoices" title="11. Linked Invoices">
        <p className="text-sm text-slate-600 mb-3">
          The Invoices tab shows all invoices matched against this PO. Matching verifies that invoiced quantities and prices match what was ordered and received (three-way match).
        </p>
      </GuideSection>

      <GuideSection id="reports" title="12. PO Reports">
        <p className="text-sm text-slate-600">
          PO reports provide listings filterable by status, supplier, date range, value, and more. Reports include both header and line-level views.
        </p>
      </GuideSection>
    </GuideViewer>
  );
}

export function PurchaseOrdersGuidePage() { return <AppShell><POContent /></AppShell>; }
export function PurchaseOrdersGuideEmbed() { return <POContent />; }

/* ═══════════════════════════════════════════════════════════════
   ASSET REGISTER GUIDE
   ═══════════════════════════════════════════════════════════════ */
function AssetContent() {
  const toc = [
    { id: 'overview', label: 'What is the Asset Register?' },
    { id: 'listing', label: 'Asset Listing' },
    { id: 'creating', label: 'Creating an Asset' },
    { id: 'depreciation', label: 'Depreciation Methods' },
    { id: 'lifecycle', label: 'Asset Lifecycle' },
    { id: 'reports', label: 'Asset Reports' },
  ];

  return (
    <GuideViewer title="Asset Register Module" subtitle="Fixed Asset Management User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. What is the Asset Register?">
        <p className="text-sm text-slate-600 mb-3">
          The Asset Register tracks your organisation's fixed assets — items of significant value that are capitalised rather than expensed. This includes equipment, machinery, vehicles, furniture, and IT hardware. The module manages the full asset lifecycle from acquisition through depreciation to disposal.
        </p>
      </GuideSection>

      <GuideSection id="listing" title="2. Asset Listing">
        <GuideFieldTable fields={[
          { name: 'Asset Code', desc: 'Unique identifier for the asset (e.g., "AST-LAP-0042").' },
          { name: 'Description', desc: 'What the asset is.' },
          { name: 'Category', desc: 'Classification (IT Equipment, Furniture, Vehicles, etc.).' },
          { name: 'Location', desc: 'Where the asset is physically located.' },
          { name: 'Purchase Value', desc: 'Original acquisition cost.' },
          { name: 'Current NBV', desc: 'Net Book Value after accumulated depreciation.' },
          { name: 'Status', desc: 'Active, Disposed, Written Off, Under Maintenance.' },
          { name: 'Depreciation Method', desc: 'How the asset is being depreciated.' },
        ]} />
      </GuideSection>

      <GuideSection id="creating" title="3. Creating an Asset">
        <GuideFieldTable fields={[
          { name: 'Asset Code', desc: 'Can be auto-generated or manually assigned.' },
          { name: 'Description', desc: 'Detailed description of the asset.' },
          { name: 'Category', desc: 'Select from asset categories (defines default depreciation rules).' },
          { name: 'Purchase Date', desc: 'When the asset was acquired.' },
          { name: 'Purchase Value', desc: 'Original cost.' },
          { name: 'Residual Value', desc: 'Expected value at the end of useful life.' },
          { name: 'Useful Life (months)', desc: 'How long the asset is expected to be productive.' },
          { name: 'Depreciation Method', desc: 'Straight-Line, Declining Balance, or Units-of-Production.' },
          { name: 'Location / Department', desc: 'Physical location and responsible department.' },
          { name: 'Serial Number', desc: 'Manufacturer\'s serial number for tracking.' },
          { name: 'Linked PO', desc: 'Link to the purchase order that acquired this asset.' },
        ]} />
      </GuideSection>

      <GuideSection id="depreciation" title="4. Depreciation Methods">
        <GuideFieldTable fields={[
          { name: 'Straight-Line', desc: 'Equal depreciation each period. Formula: (Purchase Value - Residual Value) / Useful Life.' },
          { name: 'Declining Balance', desc: 'Higher depreciation in early years. Applies a fixed percentage to the remaining book value each period.' },
          { name: 'Units of Production', desc: 'Depreciation based on usage (e.g., miles driven, hours operated). Rate per unit x units used.' },
        ]} />
        <p className="text-sm text-slate-600 mt-2">
          The Depreciation Schedule tab on each asset shows the period-by-period depreciation calculation and cumulative values.
        </p>
      </GuideSection>

      <GuideSection id="lifecycle" title="5. Asset Lifecycle">
        <ol className="list-decimal list-inside text-sm text-slate-600 space-y-2">
          <li><strong>Acquisition</strong> — Asset is purchased and registered.</li>
          <li><strong>Active Use</strong> — Asset is in service, depreciating over time.</li>
          <li><strong>Maintenance</strong> — Asset taken out of service for repair.</li>
          <li><strong>Disposal</strong> — Asset sold, scrapped, or donated. Disposal proceeds are recorded.</li>
          <li><strong>Write-Off</strong> — Asset written off the books (e.g., lost or destroyed).</li>
        </ol>
      </GuideSection>

      <GuideSection id="reports" title="6. Asset Reports">
        <p className="text-sm text-slate-600">
          Reports include asset register listings, depreciation schedules, NBV summaries by category, disposal logs, and fully-depreciated asset lists.
        </p>
      </GuideSection>
    </GuideViewer>
  );
}

export function AssetRegisterGuidePage() { return <AppShell><AssetContent /></AppShell>; }
export function AssetRegisterGuideEmbed() { return <AssetContent />; }

/* ═══════════════════════════════════════════════════════════════
   INVOICE MATCHING GUIDE
   ═══════════════════════════════════════════════════════════════ */
function InvoiceContent() {
  const toc = [
    { id: 'overview', label: 'What is Invoice Matching?' },
    { id: 'dashboard', label: 'Invoice Dashboard' },
    { id: 'three-way', label: 'Three-Way Matching' },
    { id: 'ingestion', label: 'Invoice Ingestion' },
    { id: 'manual', label: 'Manual Invoice Entry' },
    { id: 'matching', label: 'The Matching Process' },
    { id: 'approval', label: 'Invoice Approval' },
    { id: 'payment', label: 'Payment Tracking' },
    { id: 'reports', label: 'Invoice Reports' },
  ];

  return (
    <GuideViewer title="Invoice Matching Module" subtitle="Accounts Payable & Matching User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. What is Invoice Matching?">
        <p className="text-sm text-slate-600 mb-3">
          Invoice Matching ensures that supplier invoices are accurate before payment is authorised. The module compares invoiced amounts against what was ordered (Purchase Order) and what was actually received (Goods Receipt Note). This "three-way match" prevents overpayment, fraud, and errors.
        </p>
      </GuideSection>

      <GuideSection id="dashboard" title="2. Invoice Dashboard">
        <GuideFieldTable fields={[
          { name: 'Invoices to Process', desc: 'Number of invoices awaiting matching or approval.' },
          { name: 'Total Outstanding', desc: 'Total value of unpaid invoices.' },
          { name: 'Matched This Month', desc: 'Invoices successfully matched this month.' },
          { name: 'Match Rate', desc: 'Percentage of invoices that match within tolerance on first attempt.' },
          { name: 'Charts', desc: 'Monthly invoice volume, status breakdown, and aging analysis.' },
        ]} />
      </GuideSection>

      <GuideSection id="three-way" title="3. Three-Way Matching">
        <p className="text-sm text-slate-600 mb-3">The three documents compared are:</p>
        <GuideFieldTable fields={[
          { name: 'Purchase Order', desc: 'What was ordered: items, quantities, and agreed prices.' },
          { name: 'Goods Receipt Note', desc: 'What was received: actual quantities delivered.' },
          { name: 'Invoice', desc: 'What the supplier is billing: their quantities and prices.' },
        ]} />
        <p className="text-sm text-slate-600 mt-2">
          Matching tolerances can be configured (e.g., allow a 2% price variance). Invoices within tolerance are auto-matched; those outside tolerance require manual review.
        </p>
      </GuideSection>

      <GuideSection id="ingestion" title="4. Invoice Ingestion">
        <p className="text-sm text-slate-600 mb-3">
          Invoices can enter the system through multiple channels:
        </p>
        <GuideFieldTable fields={[
          { name: 'Email Ingestion', desc: 'Suppliers email invoices (PDF) to a dedicated address. Eyvo uses AI to extract invoice data automatically.' },
          { name: 'Supplier Portal', desc: 'Suppliers upload invoices directly through the Supplier Portal.' },
          { name: 'Manual Entry', desc: 'Users enter invoice details manually.' },
        ]} />
        <p className="text-sm text-slate-600 mt-2">
          The <strong>Ingestion Queue</strong> tab shows all ingested invoices with their processing status (Pending, Processed, Needs Review, Error).
        </p>
      </GuideSection>

      <GuideSection id="manual" title="5. Manual Invoice Entry">
        <GuideFieldTable fields={[
          { name: 'Invoice Number', desc: 'The supplier\'s invoice reference number.' },
          { name: 'Invoice Date', desc: 'Date on the invoice.' },
          { name: 'Due Date', desc: 'Payment due date.' },
          { name: 'PO Number', desc: 'The purchase order this invoice relates to.' },
          { name: 'Supplier', desc: 'Auto-populated from the PO.' },
          { name: 'Line Items', desc: 'Invoiced items with quantities and prices.' },
          { name: 'Tax / Gross Total', desc: 'Tax amount and total invoice value.' },
        ]} />
      </GuideSection>

      <GuideSection id="matching" title="6. The Matching Process">
        <p className="text-sm text-slate-600 mb-3">
          When an invoice is matched against a PO and GRN:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li><strong>FULLY MATCHED</strong> — All quantities and prices match within tolerance. Ready for payment.</li>
          <li><strong>PARTIALLY MATCHED</strong> — Some lines match, others have discrepancies requiring review.</li>
          <li><strong>UNMATCHED</strong> — Significant discrepancies. Requires investigation.</li>
          <li><strong>OVER-INVOICED</strong> — Supplier charged more than ordered or received.</li>
        </ul>
      </GuideSection>

      <GuideSection id="approval" title="7. Invoice Approval">
        <p className="text-sm text-slate-600 mb-3">
          Matched invoices may require approval before payment, following configurable rules similar to PO approvals. Criteria can include invoice value, supplier, and discrepancy amount.
        </p>
      </GuideSection>

      <GuideSection id="payment" title="8. Payment Tracking">
        <p className="text-sm text-slate-600 mb-3">
          Track payment status for each invoice: Not Paid, Partially Paid, Paid in Full. Payment dates and amounts are recorded for reconciliation.
        </p>
      </GuideSection>

      <GuideSection id="reports" title="9. Invoice Reports">
        <p className="text-sm text-slate-600">
          Reports include invoice listings, aging analysis, match rate statistics, and variance reports showing the most common discrepancy types.
        </p>
      </GuideSection>
    </GuideViewer>
  );
}

export function InvoiceMatchingGuidePage() { return <AppShell><InvoiceContent /></AppShell>; }
export function InvoiceMatchingGuideEmbed() { return <InvoiceContent />; }

/* ═══════════════════════════════════════════════════════════════
   INVENTORY CONTROL GUIDE
   ═══════════════════════════════════════════════════════════════ */
function InventoryContent() {
  const toc = [
    { id: 'overview', label: 'What is Inventory Control?' },
    { id: 'dashboard', label: 'Inventory Dashboard' },
    { id: 'stock', label: 'Stock Levels & Locations' },
    { id: 'batches', label: 'Batch Management' },
    { id: 'serials', label: 'Serial Number Tracking' },
    { id: 'cycle-counting', label: 'Cycle Counting' },
    { id: 'movements', label: 'Stock Movements' },
    { id: 'replenishment', label: 'Replenishment' },
    { id: 'analytics', label: 'Inventory Analytics' },
    { id: 'reports', label: 'Inventory Reports' },
  ];

  return (
    <GuideViewer title="Inventory Control Module" subtitle="Stock Management User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. What is Inventory Control?">
        <p className="text-sm text-slate-600 mb-3">
          Inventory Control manages the physical stock of goods your organisation holds. It tracks quantities by location, manages batches and serial numbers, runs cycle counts for accuracy verification, and provides analytics for optimising stock levels.
        </p>
      </GuideSection>

      <GuideSection id="dashboard" title="2. Inventory Dashboard">
        <GuideFieldTable fields={[
          { name: 'Total SKUs', desc: 'Number of distinct items in inventory.' },
          { name: 'Total Stock Value', desc: 'Combined value of all stock.' },
          { name: 'Below Minimum', desc: 'Items below their configured minimum stock level.' },
          { name: 'Quarantined Items', desc: 'Stock held aside for quality review.' },
          { name: 'Pending Write-Offs', desc: 'Stock awaiting approval for write-off.' },
          { name: 'Upcoming Cycle Counts', desc: 'Scheduled and overdue cycle counts.' },
          { name: 'Charts', desc: 'Stock movements (14 days), value by category, batch expiry timeline.' },
        ]} />
        <p className="text-sm text-slate-600 mt-2">
          Clicking any KPI card drills down to a filtered detail view showing the underlying records.
        </p>
      </GuideSection>

      <GuideSection id="stock" title="3. Stock Levels & Locations">
        <p className="text-sm text-slate-600 mb-3">
          The Stock tab shows current stock for each item across all locations:
        </p>
        <GuideFieldTable fields={[
          { name: 'Item Code', desc: 'The item being stocked.' },
          { name: 'Location', desc: 'Warehouse/store location code.' },
          { name: 'Current Stock', desc: 'Available quantity on hand.' },
          { name: 'Reserved', desc: 'Quantity reserved for pending orders.' },
          { name: 'Available', desc: 'Current Stock minus Reserved.' },
          { name: 'Minimum Level', desc: 'Configured reorder point.' },
          { name: 'Maximum Level', desc: 'Maximum desired stock level.' },
          { name: 'Unit Cost', desc: 'Current cost per unit.' },
          { name: 'Total Value', desc: 'Stock quantity multiplied by unit cost.' },
        ]} />
      </GuideSection>

      <GuideSection id="batches" title="4. Batch Management">
        <p className="text-sm text-slate-600 mb-3">
          Items that are batch-tracked have a Batches tab showing each batch with:
        </p>
        <GuideFieldTable fields={[
          { name: 'Batch Number', desc: 'Unique batch identifier.' },
          { name: 'Quantity', desc: 'Current quantity in this batch.' },
          { name: 'Expiry Date', desc: 'When this batch expires (for perishable goods).' },
          { name: 'Status', desc: 'Active, Expiring, Expired, Quarantined, Written Off.' },
          { name: 'Supplier', desc: 'The supplier who provided this batch.' },
        ]} />
      </GuideSection>

      <GuideSection id="serials" title="5. Serial Number Tracking">
        <p className="text-sm text-slate-600 mb-3">
          Serial-tracked items have individual serial numbers registered. Each serial has a lifecycle view showing its status (In Stock, Issued, On Hold, Returned, Scrapped) and a complete event history.
        </p>
      </GuideSection>

      <GuideSection id="cycle-counting" title="6. Cycle Counting">
        <p className="text-sm text-slate-600 mb-3">
          Cycle counts verify physical stock matches system records. The Cycle Counting tab allows you to:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>Create scheduled cycle counts by location</li>
          <li>Assign counters and due dates</li>
          <li>Record actual counts and identify discrepancies</li>
          <li>Apply adjustments to correct stock levels</li>
        </ul>
      </GuideSection>

      <GuideSection id="movements" title="7. Stock Movements">
        <p className="text-sm text-slate-600 mb-3">
          All stock changes are recorded as movements: receipts, issues, transfers, adjustments, returns, and write-offs. The movement history provides a complete audit trail.
        </p>
      </GuideSection>

      <GuideSection id="replenishment" title="8. Replenishment">
        <p className="text-sm text-slate-600 mb-3">
          The Replenishment tab shows items that have fallen below their minimum stock level and suggests reorder quantities based on configured rules.
        </p>
      </GuideSection>

      <GuideSection id="analytics" title="9. Inventory Analytics">
        <p className="text-sm text-slate-600 mb-3">
          Three analytics views are available:
        </p>
        <GuideFieldTable fields={[
          { name: 'Stock Aging', desc: 'Shows how long stock has been sitting. Highlights slow-moving and dead stock.' },
          { name: 'ABC Classification', desc: 'Pareto analysis classifying items as A (high value), B (medium), or C (low value) based on usage.' },
          { name: 'Demand Forecast', desc: 'Predictive analysis of future demand based on historical movement patterns.' },
        ]} />
      </GuideSection>

      <GuideSection id="reports" title="10. Inventory Reports">
        <p className="text-sm text-slate-600 mb-3">
          Comprehensive reports include Stock Valuation, Batch Expiry, Serial Audit Trail, Stock Movements, Write-Offs, Replenishment Performance, Cost of Goods Issued, Stock Aging, Cycle Count Variance, Period Comparison, and Valuation Audit. All support PDF, CSV, and email export.
        </p>
      </GuideSection>
    </GuideViewer>
  );
}

export function InventoryGuidePage() { return <AppShell><InventoryContent /></AppShell>; }
export function InventoryGuideEmbed() { return <InventoryContent />; }

/* ═══════════════════════════════════════════════════════════════
   GOODS RECEIVING GUIDE
   ═══════════════════════════════════════════════════════════════ */
function GRNContent() {
  const toc = [
    { id: 'overview', label: 'What is Receiving?' },
    { id: 'dashboard', label: 'GRN Dashboard' },
    { id: 'creating', label: 'Creating a GRN' },
    { id: 'recording', label: 'Recording Receipts' },
    { id: 'discrepancies', label: 'Handling Discrepancies' },
    { id: 'printing', label: 'Printing GRN Documents' },
    { id: 'reports', label: 'GRN Reports' },
  ];

  return (
    <GuideViewer title="Receiving Module" subtitle="Goods Receipt Note (GRN) User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. What is Receiving?">
        <p className="text-sm text-slate-600 mb-3">
          Receiving records the physical receipt of goods against purchase orders. When a delivery arrives, a Goods Received Note (GRN) is created documenting what was received, any discrepancies, and any damage or quality issues. GRNs are a critical link in three-way invoice matching.
        </p>
      </GuideSection>

      <GuideSection id="dashboard" title="2. GRN Dashboard">
        <GuideFieldTable fields={[
          { name: 'Total GRNs', desc: 'Total goods receipt notes recorded.' },
          { name: 'This Month', desc: 'GRNs created this month.' },
          { name: 'Pending Orders', desc: 'POs with outstanding deliveries.' },
          { name: 'Value Received', desc: 'Total value of goods received.' },
          { name: 'Charts', desc: 'Monthly GRN trend, top suppliers, and receipts by status.' },
        ]} />
      </GuideSection>

      <GuideSection id="creating" title="3. Creating a GRN">
        <p className="text-sm text-slate-600 mb-3">
          Navigate to a Purchase Order and click the <strong>Receiving</strong> tab, then <strong>+ Receive Goods</strong>. Alternatively, go to the Receiving module and click <strong>+ New GRN</strong>, then select the PO.
        </p>
      </GuideSection>

      <GuideSection id="recording" title="4. Recording Receipts">
        <GuideFieldTable fields={[
          { name: 'PO Line Items', desc: 'Each ordered item is shown with its ordered quantity and previously received quantity.' },
          { name: 'Quantity Received', desc: 'Enter the quantity received in this delivery.' },
          { name: 'Delivery Note', desc: 'Optional reference from the carrier\'s delivery documentation.' },
          { name: 'Comments', desc: 'Notes about the delivery (condition, packaging, etc.).' },
          { name: 'Received By', desc: 'The person who physically received the goods.' },
          { name: 'Received Date', desc: 'Date of receipt (defaults to today).' },
        ]} />
      </GuideSection>

      <GuideSection id="discrepancies" title="5. Handling Discrepancies">
        <p className="text-sm text-slate-600 mb-3">
          If the received quantity differs from the ordered quantity:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li><strong>Short Delivery</strong> — Less received than ordered. The PO remains open for the outstanding balance.</li>
          <li><strong>Over Delivery</strong> — More received than ordered. Requires decision on acceptance.</li>
          <li><strong>Damaged Goods</strong> — Record in comments. May require a return or credit note.</li>
        </ul>
      </GuideSection>

      <GuideSection id="printing" title="6. Printing GRN Documents">
        <p className="text-sm text-slate-600 mb-3">
          Each GRN can be printed as a formal document for internal records. Click the <strong>Print</strong> button on the GRN detail to generate a professional PDF showing all received items, quantities, and signatures.
        </p>
      </GuideSection>

      <GuideSection id="reports" title="7. GRN Reports">
        <p className="text-sm text-slate-600">
          GRN reports show receipt history, supplier delivery performance, and outstanding PO balances. Reports support CSV, PDF, and email export.
        </p>
      </GuideSection>
    </GuideViewer>
  );
}

export function GoodsReceivingGuidePage() { return <AppShell><GRNContent /></AppShell>; }
export function GoodsReceivingGuideEmbed() { return <GRNContent />; }

/* ═══════════════════════════════════════════════════════════════
   REPORTS SYSTEM GUIDE
   ═══════════════════════════════════════════════════════════════ */
function ReportsContent() {
  const toc = [
    { id: 'overview', label: 'Reports System Overview' },
    { id: 'module-reports', label: 'Module Reports' },
    { id: 'filters', label: 'Filtering & Advanced Criteria' },
    { id: 'export', label: 'Export Options' },
    { id: 'scheduled', label: 'Scheduled Reports' },
    { id: 'creating-schedule', label: 'Creating a Scheduled Report' },
    { id: 'managing', label: 'Managing Scheduled Reports' },
  ];

  return (
    <GuideViewer title="Reports & Scheduled Reports" subtitle="Reporting System User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. Reports System Overview">
        <p className="text-sm text-slate-600 mb-3">
          Eyvo provides comprehensive reporting across every module. Reports combine flexible filtering with professional output formats (PDF, CSV, email). You can run reports on-demand or schedule them for automatic recurring delivery.
        </p>
      </GuideSection>

      <GuideSection id="module-reports" title="2. Module Reports">
        <p className="text-sm text-slate-600 mb-3">
          Each module (Data Files, Purchase Orders, Requisitions, Invoices, etc.) has its own set of reports accessible from the <strong>Reports</strong> tab on the module listing page. Reports are presented as cards that you click to run.
        </p>
        <p className="text-sm text-slate-600 mb-2">
          When a report runs, it shows:
        </p>
        <GuideFieldTable fields={[
          { name: 'Summary Cards', desc: 'Key aggregates (counts, totals, averages) displayed as cards above the data table.' },
          { name: 'Data Table', desc: 'The full dataset in a sortable, searchable table with inline filters.' },
          { name: 'Toolbar', desc: 'Export buttons (CSV, PDF, Email, Schedule) and a Re-Run button to refresh.' },
        ]} />
      </GuideSection>

      <GuideSection id="filters" title="3. Filtering & Advanced Criteria">
        <p className="text-sm text-slate-600 mb-3">
          Each report has filter fields specific to its data type. Common filters include:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>Code or name text search</li>
          <li>Date range (from/to)</li>
          <li>Status dropdown</li>
          <li>Active/inactive toggle</li>
          <li>Supplier, category, or department selection</li>
        </ul>
        <p className="text-sm text-slate-600 mt-2">
          Click <strong>Re-Run</strong> after changing filters to refresh the data.
        </p>
      </GuideSection>

      <GuideSection id="export" title="4. Export Options">
        <GuideFieldTable fields={[
          { name: 'CSV', desc: 'Downloads all rows and ALL database columns (not just visible ones) as a CSV file. Ideal for analysis in Excel or Google Sheets.' },
          { name: 'PDF', desc: 'Generates a professionally formatted PDF with Eyvo logo, headers, and page numbers. Suitable for distribution and archiving.' },
          { name: 'Email', desc: 'Generates a PDF and opens an email dialog to send it directly to recipients.' },
          { name: 'Schedule', desc: 'Opens the schedule dialog to set up recurring automated delivery.' },
        ]} />
      </GuideSection>

      <GuideSection id="scheduled" title="5. Scheduled Reports">
        <p className="text-sm text-slate-600 mb-3">
          Scheduled Reports allow you to configure reports that run automatically and are delivered via email on a recurring basis. Access the Scheduled Reports module from the left sidebar.
        </p>
      </GuideSection>

      <GuideSection id="creating-schedule" title="6. Creating a Scheduled Report">
        <GuideFieldTable fields={[
          { name: 'Report Name', desc: 'A friendly label for this schedule (e.g., "Weekly Supplier Listing").' },
          { name: 'Module / Report', desc: 'Which module report to run (pre-filled if created from a report\'s Schedule button).' },
          { name: 'Frequency', desc: 'How often to run: Daily, Weekly (select day), or Monthly (select date).' },
          { name: 'Time', desc: 'What time of day to run the report (in your system timezone).' },
          { name: 'Recipients', desc: 'Email addresses to send the report to. Multiple recipients supported.' },
          { name: 'Format', desc: 'PDF or CSV attachment.' },
          { name: 'Filters', desc: 'Pre-configured filter criteria that are applied each time the report runs.' },
          { name: 'Active', desc: 'Toggle to enable or disable the schedule without deleting it.' },
        ]} />
      </GuideSection>

      <GuideSection id="managing" title="7. Managing Scheduled Reports">
        <p className="text-sm text-slate-600 mb-3">
          The Scheduled Reports page lists all configured schedules with their next run time, frequency, and status. You can edit, pause, resume, or delete any schedule.
        </p>
        <GuideCallout type="tip" title="Delivery Timezone">
          <p>Scheduled report times use your system timezone. If your timezone is set to "America/Los_Angeles" and you schedule a report for 8:00 AM, it runs at 8:00 AM Pacific Time.</p>
        </GuideCallout>
      </GuideSection>
    </GuideViewer>
  );
}

export function ReportsGuidePage() { return <AppShell><ReportsContent /></AppShell>; }
export function ReportsGuideEmbed() { return <ReportsContent />; }
